import {
	createContext,
	ReactNode,
	useContext,
	useEffect,
	useState,
} from "react";
import {
	applyThemeConfig,
	fetchThemeConfig,
} from "../services/themeConfigService";

const PUBLIC_PATH = import.meta.env.VITE_PUBLIC_PATH ?? "/";
const DEFAULT_LOGO_LOGIN_FILE = "logo-app-login.svg";
const DEFAULT_LOGO_MENU_FILE = "logo-app-menu.svg";
const DEFAULT_FAVICON_FILE = "favicon.ico";
export const DEFAULT_FAVICON_FILE_IN_THEME = "logo-favicon.ico";

const DEFAULT_LOGO_LOGIN_FILEPATH = `${PUBLIC_PATH}assets/${DEFAULT_LOGO_LOGIN_FILE}`;
const DEFAULT_LOGO_MENU_FILEPATH = `${PUBLIC_PATH}assets/${DEFAULT_LOGO_MENU_FILE}`;
const DEFAULT_LOGO_FAVICON_FILEPATH = `${PUBLIC_PATH}assets/${DEFAULT_FAVICON_FILE}`;
const DEFAULT_APP_NAME = "Wisephere";

interface ThemeConfigContextValue {
	centeredLogin: boolean;
	customLogo: boolean;
	logoLogin: string;
	logoMenu: string;
	enterpriseName: string;
	favicon: string;
}

const ThemeConfigContext = createContext<ThemeConfigContextValue>({
	centeredLogin: true,
	customLogo: false,
	logoLogin: DEFAULT_LOGO_LOGIN_FILEPATH,
	logoMenu: DEFAULT_LOGO_MENU_FILEPATH,
	enterpriseName: DEFAULT_APP_NAME,
	favicon: DEFAULT_LOGO_FAVICON_FILEPATH,
});

export function ThemeConfigProvider({
	children,
}: {
	children: ReactNode;
}) {
	const [centeredLogin, setCenteredLogin] = useState(false);
	const [customLogo, setCustomLogo] = useState(false);
	const [logoLogin, setLogoLogin] = useState(DEFAULT_LOGO_LOGIN_FILEPATH);
	const [logoMenu, setLogoMenu] = useState(DEFAULT_LOGO_MENU_FILEPATH);
	const [favicon, setFavicon] = useState(DEFAULT_LOGO_FAVICON_FILEPATH);
	const [enterpriseName, setEnterpriseName] = useState("");

	useEffect(() => {
		fetchThemeConfig().then((config) => {
			if (!config) return;
			applyThemeConfig(config);
			setCenteredLogin(config.centeredLogin ?? false);
			setCustomLogo(config.customLogo ?? false);
			if (config.enterpriseName) setEnterpriseName(config.enterpriseName);
			if (config.customLogo) {
				setLogoLogin(
					config.logoLogin
						? `${PUBLIC_PATH}assets/theme/${config.logoLogin}`
						: "",
				);
				setLogoMenu(
					config.logoMenu
						? `${PUBLIC_PATH}assets/theme/${config.logoMenu}`
						: "",
				);
			}
			setFavicon(
				config.favicon && config.favicon !== ""
					? `${PUBLIC_PATH}assets/theme/${DEFAULT_FAVICON_FILE_IN_THEME}`
					: "",
			);
		});
	}, []);

	useEffect(() => {
		const link = document.querySelector("link[rel~='icon']");
		if (link && favicon) {
			link.setAttribute("href", favicon);
		}
	}, [favicon]);

	return (
		<ThemeConfigContext.Provider
			value={{
				centeredLogin,
				customLogo,
				logoLogin,
				logoMenu,
				enterpriseName,
				favicon,
			}}
		>
			{children}
		</ThemeConfigContext.Provider>
	);
}

export function useThemeConfig(): ThemeConfigContextValue {
	return useContext(ThemeConfigContext);
}
